'use client';

// AI section hidden — keep import commented for easy restore.
// import AccountAiWorkflowPanel from '@/components/auth/AccountAiWorkflowPanel';
import AccountToolLibraryPanel from '@/components/auth/AccountToolLibraryPanel';
import { useFirebaseAuth } from '@/components/auth/FirebaseAuthProvider';
import ThemePreferencePanel from '@/components/ThemePreferencePanel';
import AccountPwaInstallPanel from '@/components/pwa/AccountPwaInstallPanel';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import type { ReactNode, SVGProps } from 'react';

type AccountSectionId = 'general' | 'account' | 'tools' | 'ai' | 'app';

type LocalAccountSettings = {
  preferredName: string;
  usageAlerts: boolean;
  productUpdates: boolean;
};

type IconProps = SVGProps<SVGSVGElement>;

function SectionIcon(props: IconProps & { name: AccountSectionId }) {
  const { name, ...rest } = props;
  const commonProps: IconProps = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    ...rest,
  };

  switch (name) {
    case 'general':
      return (
        <svg {...commonProps}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      );
    case 'account':
      return (
        <svg {...commonProps}>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    case 'tools':
      return (
        <svg {...commonProps}>
          <path d="M14.7 6.3a4 4 0 0 0-5.6 5.6L3 18l3 3 6.1-6.1a4 4 0 0 0 5.6-5.6l-2.3 2.3-2.4-2.4z" />
        </svg>
      );
    case 'ai':
      return (
        <svg {...commonProps}>
          <path d="M12 2v4" />
          <path d="M12 18v4" />
          <path d="M4.93 4.93l2.83 2.83" />
          <path d="M16.24 16.24l2.83 2.83" />
          <path d="M2 12h4" />
          <path d="M18 12h4" />
          <path d="M4.93 19.07l2.83-2.83" />
          <path d="M16.24 7.76l2.83-2.83" />
          <circle cx="12" cy="12" r="4" />
        </svg>
      );
    case 'app':
      return (
        <svg {...commonProps}>
          <rect x="6" y="2" width="12" height="20" rx="2" />
          <path d="M11 18h2" />
        </svg>
      );
    default:
      return null;
  }
}

const accountSections: Array<{ id: AccountSectionId; label: string }> = [
  { id: 'general', label: 'General' },
  { id: 'account', label: 'Account' },
  { id: 'tools', label: 'Tools' },
  // AI section hidden — restore by re-adding { id: 'ai', label: 'AI' } here.
  { id: 'app', label: 'App' },
];

function AccountNotice({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="account-inline-notice" role="status">
      <strong>{title}</strong>
      <p>{children}</p>
    </div>
  );
}

function AccountDetailRow({
  label,
  value,
  note,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
}) {
  return (
    <div className="account-detail-row">
      <span className="account-detail-label">{label}</span>
      <div className="account-detail-value">
        <strong>{value}</strong>
        {note ? <p>{note}</p> : null}
      </div>
    </div>
  );
}

function AccountField({
  label,
  hint,
  fullWidth = false,
  children,
}: {
  label: string;
  hint?: string;
  fullWidth?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`account-form-field ${fullWidth ? 'account-form-field-full' : ''}`.trim()}>
      <span className="account-form-label">{label}</span>
      {children}
      {hint ? <span className="account-form-hint">{hint}</span> : null}
    </label>
  );
}

function AccountSwitchRow({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      aria-checked={checked}
      className="account-switch-row"
      onClick={onToggle}
      role="switch"
      type="button"
    >
      <span className="account-switch-copy">
        <strong>{label}</strong>
      </span>
      <span className={`account-switch ${checked ? 'account-switch-active' : ''}`.trim()}>
        <span className="account-switch-thumb" />
      </span>
    </button>
  );
}

function formatDateLabel(value: string | null | undefined) {
  if (!value) {
    return 'Not available';
  }

  const timestamp = Date.parse(value);

  if (!Number.isFinite(timestamp)) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
  }).format(timestamp);
}

function getPreferredName(displayName: string, email: string | null | undefined) {
  const trimmedDisplayName = displayName.trim();

  if (trimmedDisplayName) {
    return trimmedDisplayName.split(/\s+/)[0] ?? trimmedDisplayName;
  }

  if (email) {
    return email.split('@')[0] ?? 'Cleanor';
  }

  return 'Cleanor';
}

function getLocalSettingsStorageKey(uid: string) {
  return `cleanor-account-settings:${uid}`;
}

function buildDefaultLocalSettings(
  displayName: string,
  email: string | null | undefined,
): LocalAccountSettings {
  return {
    preferredName: getPreferredName(displayName, email),
    usageAlerts: true,
    productUpdates: false,
  };
}

function readStoredLocalSettings(
  uid: string,
  defaults: LocalAccountSettings,
): LocalAccountSettings {
  try {
    const rawValue = window.localStorage.getItem(getLocalSettingsStorageKey(uid));

    if (!rawValue) {
      return defaults;
    }

    const parsedValue = JSON.parse(rawValue) as Partial<LocalAccountSettings>;

    return {
      preferredName:
        typeof parsedValue.preferredName === 'string'
          ? parsedValue.preferredName
          : defaults.preferredName,
      usageAlerts:
        typeof parsedValue.usageAlerts === 'boolean'
          ? parsedValue.usageAlerts
          : defaults.usageAlerts,
      productUpdates:
        typeof parsedValue.productUpdates === 'boolean'
          ? parsedValue.productUpdates
          : defaults.productUpdates,
    };
  } catch {
    return defaults;
  }
}

export default function AccountPanel() {
  const { user, isLoading, session, isSessionLoading, errorMessage, firebaseEnabled, signOutUser } =
    useFirebaseAuth();
  const [activeSection, setActiveSection] = useState<AccountSectionId>('general');
  const [localSettings, setLocalSettings] = useState<LocalAccountSettings | null>(null);
  const [hasLoadedLocalSettings, setHasLoadedLocalSettings] = useState(false);
  const isAccessLoading = isLoading || isSessionLoading;
  const displayName = user?.displayName || session?.account?.displayName || 'Cleanor account';

  useEffect(() => {
    if (!user) {
      setLocalSettings(null);
      setHasLoadedLocalSettings(false);
      return;
    }

    const defaults = buildDefaultLocalSettings(displayName, user.email);
    const storedSettings = readStoredLocalSettings(user.uid, defaults);
    setLocalSettings(storedSettings);
    setHasLoadedLocalSettings(true);
  }, [displayName, user]);

  useEffect(() => {
    if (!user || !localSettings || !hasLoadedLocalSettings) {
      return;
    }

    try {
      window.localStorage.setItem(
        getLocalSettingsStorageKey(user.uid),
        JSON.stringify(localSettings),
      );
    } catch {
      // Ignore local storage write failures and keep the in-memory settings.
    }
  }, [hasLoadedLocalSettings, localSettings, user]);

  const providerLabel =
    session?.account?.signInProvider === 'google.com'
      ? 'Google'
      : session?.account?.signInProvider === 'password'
        ? 'Email'
        : session?.account?.signInProvider === 'facebook.com'
          ? 'Meta'
          : session?.account?.signInProvider === 'apple.com'
            ? 'Apple'
            : session?.identityType === 'guest'
              ? 'Guest'
              : 'Signed in';

  const memberSinceLabel = formatDateLabel(session?.account?.createdAt);

  function updateLocalSettings<K extends keyof LocalAccountSettings>(
    key: K,
    value: LocalAccountSettings[K],
  ) {
    setLocalSettings((current) => {
      const defaults = buildDefaultLocalSettings(displayName, user?.email);
      const base = current ?? defaults;

      return {
        ...base,
        [key]: value,
      };
    });
  }

  if (isAccessLoading) {
    return (
      <div className="account-panel">
        <section className="account-tab-section">
          <div className="account-section-heading">
            <h2>Loading account…</h2>
          </div>
        </section>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="account-panel">
        <section className="account-tab-section">
          <div className="account-section-heading">
            <h2>Sign in</h2>
            <p>
              Every tool is free to use without an account. Sign in to save your favorite tools and
              settings under one identity.
            </p>
          </div>

          <div className="account-inline-actions">
            <Link className="button account-submit-button" href="/login">
              Login
            </Link>
            <Link className="button button-ghost-light account-inline-button" href="/signup">
              Create account
            </Link>
          </div>

          {!firebaseEnabled ? (
            <AccountNotice title="Setup">
              Firebase Auth is not configured, so sign-in will not complete.
            </AccountNotice>
          ) : null}

          {errorMessage ? <AccountNotice title="Sign-in">{errorMessage}</AccountNotice> : null}
        </section>
      </div>
    );
  }

  return (
    <div className="account-panel">
      <div className="account-settings-frame">
        <aside className="account-sidebar">
          <div className="account-sidebar-profile">
            <div className="account-user-row">
              {user.photoURL ? (
                <img
                  alt=""
                  className="account-user-avatar"
                  referrerPolicy="no-referrer"
                  src={user.photoURL}
                />
              ) : (
                <div className="account-user-avatar account-user-avatar-fallback">
                  {(user.displayName || user.email || 'A').slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="account-user-meta">
                <strong>{displayName}</strong>
                <span>{user.email ?? 'Signed in'}</span>
              </div>
            </div>
          </div>

          <nav aria-label="Account sections" className="account-sidebar-nav">
            {accountSections.map((section) => (
              <button
                aria-pressed={activeSection === section.id}
                className={`account-sidebar-link ${activeSection === section.id ? 'account-sidebar-link-active' : ''}`}
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                type="button"
              >
                <SectionIcon name={section.id} className="account-sidebar-icon" />
                <span>{section.label}</span>
              </button>
            ))}
          </nav>

          <button
            className="account-sidebar-link account-sidebar-logout"
            onClick={() => void signOutUser()}
            type="button"
          >
            <svg
              aria-hidden="true"
              className="account-sidebar-icon"
              fill="none"
              height={18}
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.6}
              viewBox="0 0 24 24"
              width={18}
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <path d="M16 17l5-5-5-5" />
              <path d="M21 12H9" />
            </svg>
            <span>Logout</span>
          </button>
        </aside>

        <div className="account-settings-content">
          {errorMessage ? <AccountNotice title="Account">{errorMessage}</AccountNotice> : null}

          {activeSection === 'general' ? (
            <div className="account-tab-stack">
              <section className="account-tab-section">
                <div className="account-section-heading">
                  <h3>Profile</h3>
                </div>

                <div className="account-form-grid">
                  <AccountField label="Name">
                    <div className="account-input-shell">
                      {user.photoURL ? (
                        <img
                          alt=""
                          className="account-input-avatar"
                          referrerPolicy="no-referrer"
                          src={user.photoURL}
                        />
                      ) : (
                        <span className="account-input-avatar account-user-avatar-fallback">
                          {(displayName || user.email || 'A').slice(0, 1).toUpperCase()}
                        </span>
                      )}
                      <input className="account-input" readOnly type="text" value={displayName} />
                    </div>
                  </AccountField>

                  <AccountField label="Preferred name">
                    <input
                      className="account-input"
                      onChange={(event) => updateLocalSettings('preferredName', event.target.value)}
                      placeholder="What to call you"
                      type="text"
                      value={localSettings?.preferredName ?? ''}
                    />
                  </AccountField>

                  <AccountField fullWidth label="Email">
                    <input
                      className="account-input"
                      readOnly
                      type="email"
                      value={user.email ?? ''}
                    />
                  </AccountField>
                </div>
              </section>

              <section className="account-tab-section">
                <div className="account-section-heading">
                  <h3>Notifications</h3>
                </div>

                <div className="account-switch-list">
                  <AccountSwitchRow
                    checked={localSettings?.usageAlerts ?? true}
                    label="Usage alerts"
                    onToggle={() =>
                      updateLocalSettings('usageAlerts', !(localSettings?.usageAlerts ?? true))
                    }
                  />
                  <AccountSwitchRow
                    checked={localSettings?.productUpdates ?? false}
                    label="Product updates"
                    onToggle={() =>
                      updateLocalSettings(
                        'productUpdates',
                        !(localSettings?.productUpdates ?? false),
                      )
                    }
                  />
                </div>
              </section>

              <ThemePreferencePanel />
            </div>
          ) : null}

          {activeSection === 'account' ? (
            <div className="account-tab-stack">
              <section className="account-tab-section">
                <div className="account-section-heading">
                  <h3>Identity</h3>
                </div>

                <div className="account-detail-list">
                  <AccountDetailRow label="Provider" value={providerLabel} />
                  <AccountDetailRow label="Email" value={user.email ?? 'Not provided'} />
                  <AccountDetailRow
                    label="Verified"
                    value={session?.account?.emailVerified ? 'Yes' : 'No'}
                  />
                  <AccountDetailRow label="Member since" value={memberSinceLabel} />
                  <AccountDetailRow
                    label="Account ID"
                    value={session?.account?.uid ?? 'Not available'}
                  />
                </div>
              </section>

              <section className="account-tab-section">
                <div className="account-inline-actions">
                  <Link className="button button-ghost-light account-inline-button" href="/support">
                    Contact support
                  </Link>
                  <button
                    className="button button-ghost-light account-inline-button"
                    onClick={() => void signOutUser()}
                    type="button"
                  >
                    Sign out
                  </button>
                </div>
              </section>
            </div>
          ) : null}

          {activeSection === 'tools' ? <AccountToolLibraryPanel /> : null}
          {/* AI section hidden — restore: {activeSection === 'ai' ? <AccountAiWorkflowPanel /> : null} */}
          {activeSection === 'app' ? <AccountPwaInstallPanel /> : null}
        </div>
      </div>
    </div>
  );
}
